import {
  WatchlistSummaryAccount,
  WatchlistSummarySection,
} from "../../Types/WatchlistSummary";

/**
 * Data structure from your JSON file
 */
interface DigestDataItem {
  id: string;
  account_id: string;
  account_name: string;
  organization_id?: string;
  organization_name?: string | null;
  date: string;
  data: {
    about?: any;
    financial?: any;
    news?: any;
    social?: any;
    traffic?: any;
    leadership?: any;
    growth?: any;
    legal?: any;
    stack?: any;
    summary?: {
      overallAccountUpdate?: {
        accountName?: string;
        overallFlag?: "R" | "Y" | "G";
        overallScore?: number;
        overallHealthFlag?: "R" | "Y" | "G";
        overallHealthScore?: number;
        overallRelevanceFlag?: "H" | "M" | "L";
        overallRelevanceScore?: number;
        summaryStatement?: string;
        relevanceStatement?: string;
        [key: string]: any;
      };
      sectionsUpdate?: Array<{
        section_name: string;
        sectionFlag?: "R" | "Y" | "G";
        sectionScore?: number;
        sectionHealthFlag?: "R" | "Y" | "G";
        sectionHealthScore?: number;
        sectionRelevanceFlag?: "H" | "M" | "L";
        sectionRelevanceScore?: number;
        [key: string]: any;
      }>;
    };
    [key: string]: any;
  };
}

function buildAccountBriefLine(
  overall?: {
    summaryStatement?: string;
    relevanceStatement?: string;
    industry?: string;
    segment?: string;
  } | null
): string | undefined {
  if (!overall) return undefined;

  const summaryStatement = (overall.summaryStatement ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (summaryStatement) return summaryStatement;

  const relevanceStatement = (overall.relevanceStatement ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (relevanceStatement) return relevanceStatement;

  const industry = (overall.industry ?? "").trim();
  const segment = (overall.segment ?? "").trim();
  const parts = [industry, segment].filter(Boolean);
  if (parts.length) return parts.join(" · ");

  return undefined;
}

/**
 * Transform digest data from JSON to WatchlistSummaryAccount format
 */
export function transformDigestDataToAccounts(
  digestData: DigestDataItem[],
  organizationName?: string
): WatchlistSummaryAccount[] {
  return digestData.map(item => {
    const overallData = item.data.summary?.overallAccountUpdate;
    const accountBriefLine = buildAccountBriefLine(overallData);
    const sectionsUpdate = item.data.summary?.sectionsUpdate || [];

    // Transform sectionsUpdate to sectionsData format
    const sectionsData = sectionsUpdate.map(section => ({
      section_name: section.section_name,
      sectionFlag: section.sectionFlag || "Y",
      sectionScore: section.sectionScore || 0,
      sectionHealthFlag: section.sectionHealthFlag || "Y",
      sectionHealthScore: section.sectionHealthScore || 0,
      sectionRelevanceFlag: section.sectionRelevanceFlag || "M",
      sectionRelevanceScore: section.sectionRelevanceScore || 0,
    }));

    // Build fullDigestData object with all sections
    const fullDigestData: any = {
      about: item.data.about,
      financial: item.data.financial,
      news: item.data.news,
      social: item.data.social,
      traffic: item.data.traffic,
      leadership: item.data.leadership,
      growth: item.data.growth,
      legal: item.data.legal,
      stack: item.data.stack,
      summary: item.data.summary,
    };

    return {
      accountName: item.account_name || overallData?.accountName || "Unknown",
      accountId: item.account_id,
      organizationName: organizationName || item.organization_name || undefined,
      accountBriefLine,
      hasData: true,
      hasSelectedDateData: true,
      overallData: overallData
        ? {
            accountName: overallData.accountName,
            accountStockSymbol: overallData.accountStockSymbol,
            accountStockExchange: overallData.accountStockExchange,
            accountWebsiteURL: overallData.accountWebsiteURL,
            summaryStatement: overallData.summaryStatement,
            relevanceStatement: overallData.relevanceStatement,
            industry: overallData.industry,
            segment: overallData.segment,
            location: overallData.location
              ? {
                  streetAddress: overallData["location.streetAddress"],
                  city: overallData["location.city"],
                  state_province: overallData["location.state_province"],
                  zip_pin: overallData["location.zip_pin"],
                  country: overallData["location.country"],
                  region: overallData["location.region"],
                  continent: overallData["location.continent"],
                }
              : undefined,
            overallFlag: overallData.overallFlag || "Y",
            overallScore: overallData.overallScore || 0,
            overallHealthFlag: overallData.overallHealthFlag || "Y",
            overallHealthScore: overallData.overallHealthScore || 0,
            overallRelevanceFlag: overallData.overallRelevanceFlag || "M",
            overallRelevanceScore: overallData.overallRelevanceScore || 0,
          }
        : undefined,
      sectionsData,
      fullDigestData,
    };
  });
}

/**
 * Extract sections configuration from digest data
 */
export function extractSectionsFromDigestData(
  digestData: DigestDataItem[]
): WatchlistSummarySection[] {
  const sectionMap = new Map<string, WatchlistSummarySection>();
  const sectionOrder: Record<string, number> = {
    about: 1,
    financial: 2,
    news: 3,
    leadership: 4,
    legal: 5,
    social: 6,
    traffic: 7,
    growth: 8,
    stack: 9,
    summary: 10,
  };

  const sectionNames: Record<string, string> = {
    about: "About Company",
    financial: "Financial Overview",
    news: "News Analysis",
    leadership: "Leadership Team",
    legal: "Legal & Compliance",
    social: "Social Media",
    traffic: "Digital Traffic",
    growth: "Growth Signals",
    stack: "Technology Stack",
    summary: "Summary",
  };

  const sectionIcons: Record<string, string> = {
    about: "🏢",
    financial: "💰",
    news: "📰",
    leadership: "👥",
    legal: "⚖️",
    social: "📱",
    traffic: "🚀",
    growth: "📈",
    stack: "💻",
    summary: "📊",
  };

  // Process all digest items to find available sections
  digestData.forEach(item => {
    const sectionsUpdate = item.data.summary?.sectionsUpdate || [];
    sectionsUpdate.forEach(section => {
      const sectionName = section.section_name;
      if (sectionName && !sectionMap.has(sectionName)) {
        sectionMap.set(sectionName, {
          id: sectionName,
          name: sectionNames[sectionName] || sectionName,
          enabled: true,
          display_order: sectionOrder[sectionName] || 99,
          icon_name: sectionIcons[sectionName] || "📊",
        });
      }
    });
  });

  // Also check data keys for sections that might not be in sectionsUpdate
  digestData.forEach(item => {
    Object.keys(item.data).forEach(key => {
      if (key !== "summary" && !sectionMap.has(key) && sectionNames[key]) {
        sectionMap.set(key, {
          id: key,
          name: sectionNames[key],
          enabled: true,
          display_order: sectionOrder[key] || 99,
          icon_name: sectionIcons[key] || "📊",
        });
      }
    });
  });

  return Array.from(sectionMap.values()).sort(
    (a, b) => a.display_order - b.display_order
  );
}
